import React from "react";
import { useSelector } from "react-redux";
import { Close, ArrowBackIosNew, ArrowForwardIos, Logout } from "@mui/icons-material";
import {
    List,
    ListItemIcon,
    ListItemText,
    Collapse,
    Tooltip,
    Popover,
    Drawer as DrawerMUI,
    ListItemButton,
    Grid2 as Grid,
    IconButton,
    Box,
    Typography,
    Avatar,
    Badge,
} from "@mui/material";
import { Link } from "react-router-dom";
import { dispatch } from "../../../App";
import {
    DrawerMainLayoutClose,
    DrawerMainLayoutOpen,
} from "../../../Reducer/DrawerMainLayoutReducer/DrawerMainLayoutSlice";
import { getRoute, getRouteByPathname } from "../../../Handler/RouteHandler";
import { getMenu } from "../../../Handler/MenuHandler";
import { AuthLogout } from "../../../Reducer/AuthReducer/AuthSlice";
import ApiHandler from "../../../Handler/ApiHandler";

const drawerOpenWidth = 296;
const drawerCloseWidth = 92;
const notificationPollIntervalMs = 30000;
const defaultDocumentTitle = "(inout) Skin.";

function Drawer() {
    const drawerMainLayoutReducer = useSelector((state) => state.DrawerMainLayoutReducer);
    const screenReducer = useSelector((state) => state.ScreenReducer);
    const authReducer = useSelector((state) => state.AuthReducer);

    const [expanded, setExpanded] = React.useState("");
    const [expandedPopover, setExpandedPopover] = React.useState({});
    const [route, setRoute] = React.useState({});
    const [showDrawerFull, setShowDrawerFull] = React.useState(true);
    const [publicAppointmentNotificationCount, setPublicAppointmentNotificationCount] = React.useState(0);

    const pathname = window.location.pathname;
    const userRoles = authReducer.roles || [];
    const canReceivePublicAppointmentNotifications =
        userRoles.includes("ROLE_CRM") || userRoles.includes("ROLE_SUPER_ADMIN");

    React.useEffect(() => {
        if (!showDrawerFull) dispatch(DrawerMainLayoutClose());
        const currentRoute = getRouteByPathname(pathname) || {};
        setRoute(currentRoute);
        setExpanded(currentRoute.id || "");
        setExpandedPopover({});
    }, [pathname, showDrawerFull]);

    React.useEffect(() => {
        if (screenReducer.screen) {
            const isDrawerFull =
                screenReducer.screen === "MD" ||
                screenReducer.screen === "LG" ||
                screenReducer.screen === "XL";
            if (isDrawerFull !== showDrawerFull) {
                if (!isDrawerFull) dispatch(DrawerMainLayoutClose());
                setShowDrawerFull(isDrawerFull);
            }
        }
    }, [screenReducer.screen, showDrawerFull]);

    const changeDrawer = (isOpenDrawer) => {
        if (isOpenDrawer) dispatch(DrawerMainLayoutOpen());
        else dispatch(DrawerMainLayoutClose());
    };

    const onClickTooltip = (id, event) => {
        if (drawerMainLayoutReducer.isOpen) {
            setExpanded(id === expanded ? "" : id);
        } else {
            setExpandedPopover(expandedPopover.id === id ? {} : { id: id, anchorEl: event.currentTarget });
        }
    };

    const logout = () => {
        dispatch(AuthLogout());
        ApiHandler.get({ route: "authentication_logout" }, (response) => { });
    };

    const userInitials = `${(authReducer.firstname || "").charAt(0)}${(authReducer.lastname || "").charAt(0)}`
        .toUpperCase();
    const displayName = [authReducer.firstname, authReducer.lastname].filter(Boolean).join(" ");
    const notificationBadgeContent = publicAppointmentNotificationCount > 99 ? "99+" : publicAppointmentNotificationCount;

    const fetchPublicAppointmentNotificationCount = React.useCallback(() => {
        if (!canReceivePublicAppointmentNotifications) {
            setPublicAppointmentNotificationCount(0);
            return;
        }

        ApiHandler.get({ route: "crm_appointment_notification_count" }, (response) => {
            if (response && response.status === 200) {
                const nextCount = Number(response.data?.count ?? 0);
                setPublicAppointmentNotificationCount(Number.isNaN(nextCount) ? 0 : Math.max(0, nextCount));
            }
        });
    }, [canReceivePublicAppointmentNotifications]);

    React.useEffect(() => {
        fetchPublicAppointmentNotificationCount();
        const intervalId = window.setInterval(fetchPublicAppointmentNotificationCount, notificationPollIntervalMs);

        return () => window.clearInterval(intervalId);
    }, [fetchPublicAppointmentNotificationCount]);

    React.useEffect(() => {
        const refreshNotifications = () => {
            fetchPublicAppointmentNotificationCount();
        };

        window.addEventListener("crm-appointment-notifications-refresh", refreshNotifications);
        return () => {
            window.removeEventListener("crm-appointment-notifications-refresh", refreshNotifications);
        };
    }, [fetchPublicAppointmentNotificationCount]);

    React.useEffect(() => {
        if (!canReceivePublicAppointmentNotifications) {
            document.title = defaultDocumentTitle;
            return;
        }

        document.title =
            publicAppointmentNotificationCount > 0
                ? `(${notificationBadgeContent}) ${defaultDocumentTitle}`
                : defaultDocumentTitle;
    }, [canReceivePublicAppointmentNotifications, notificationBadgeContent, publicAppointmentNotificationCount]);

    React.useEffect(() => {
        return () => {
            document.title = defaultDocumentTitle;
        };
    }, []);

    const getMainItemSx = (isSelected) => ({
        minHeight: "48px",
        borderRadius: "12px",
        padding: drawerMainLayoutReducer.isOpen ? "8px 12px" : "8px",
        justifyContent: drawerMainLayoutReducer.isOpen ? "flex-start" : "center",
        gap: "10px",
        color: isSelected ? "#4e3a32" : "#695a53",
        backgroundColor: isSelected ? "rgba(228, 191, 179, 0.58)" : "transparent",
        border: isSelected ? "1px solid rgba(164, 139, 129, 0.35)" : "1px solid transparent",
        transition: "all 180ms ease-in-out",
        "&:hover": {
            color: "#4e3a32",
            backgroundColor: "rgba(228, 191, 179, 0.42)",
            borderColor: "rgba(164, 139, 129, 0.30)",
            "& .MuiListItemIcon-root": {
                color: "#5d463c",
            },
        },
        "& .MuiListItemIcon-root": {
            minWidth: 0,
            justifyContent: "center",
            color: isSelected ? "#5d463c" : "#7b6e67",
            marginRight: drawerMainLayoutReducer.isOpen ? "2px" : 0,
        },
        "& .MuiTypography-root": {
            overflowX: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "14px",
            fontWeight: 700,
            letterSpacing: "0.01em",
        },
    });

    const getChildItemSx = (isSelected) => ({
        minHeight: "38px",
        borderRadius: "10px",
        padding: "6px 10px",
        marginLeft: "18px",
        marginRight: "4px",
        color: isSelected ? "#4f3b32" : "#756761",
        backgroundColor: isSelected ? "rgba(228, 191, 179, 0.52)" : "transparent",
        border: isSelected ? "1px solid rgba(164, 139, 129, 0.30)" : "1px solid transparent",
        transition: "all 160ms ease",
        "&:hover": {
            color: "#4f3b32",
            backgroundColor: "rgba(228, 191, 179, 0.38)",
        },
        "& .MuiTypography-root": {
            overflowX: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "13px",
            fontWeight: 600,
        },
    });

    const canAccessRoute = (menuElement) => {
        const userRoles = authReducer.roles || [];
        const currentRoute = getRoute(menuElement.id);
        return (
            userRoles.includes("ROLE_SUPER_ADMIN") ||
            (currentRoute && currentRoute.roles && currentRoute.roles.some((role) => userRoles.includes(role)))
        );
    };

    const getMenuLabel = (menuItem) => {
        if (menuItem.id === "crm_appointment_list" && publicAppointmentNotificationCount > 0) {
            return `${menuItem.label} (${notificationBadgeContent})`;
        }

        return menuItem.label;
    };

    const renderMenuIcon = (menuId, icon) => {
        if (menuId !== "crm" || publicAppointmentNotificationCount < 1) {
            return icon;
        }

        return (
            <Badge
                color="error"
                badgeContent={notificationBadgeContent}
                max={99}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                    "& .MuiBadge-badge": {
                        fontWeight: 700,
                        fontSize: "10px",
                        minWidth: "16px",
                        height: "16px",
                    },
                }}
            >
                {icon}
            </Badge>
        );
    };

    const getCollapse = (element) => {
        return (
            <Collapse
                in={expanded.startsWith(element.id)}
                timeout="auto"
                unmountOnExit
                sx={{
                    "& .MuiCollapse-wrapperInner": {
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        paddingTop: "4px",
                    },
                }}
            >
                {element.children.map((children) => (
                    <Link to={children.path} key={children.id}>
                        <ListItemButton
                            className={"collapse-listItemButton"}
                            selected={children.id === route.id}
                            sx={getChildItemSx(children.id === route.id)}
                        >
                            <ListItemText primary={getMenuLabel(children)} />
                        </ListItemButton>
                    </Link>
                ))}
            </Collapse>
        );
    };

    const getPopover = (element) => {
        return (
            <Popover
                id={element.id}
                open={expandedPopover.id === element.id}
                anchorEl={expandedPopover.anchorEl}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                onClose={() => setExpandedPopover({})}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "14px",
                            border: "1px solid rgba(164, 139, 129, 0.25)",
                            boxShadow: "0 16px 36px rgba(86, 67, 58, 0.16)",
                            background: "linear-gradient(180deg, #ffffff 0%, #fbf7f5 100%)",
                            padding: "6px",
                            minWidth: "212px",
                        },
                    },
                }}
            >
                {element.children.map((children) => (
                    <Link to={children.path} key={children.id}>
                        <ListItemButton
                            className={"popover-listItemButton"}
                            selected={children.id === route.id}
                            sx={{
                                ...getChildItemSx(children.id === route.id),
                                marginLeft: 0,
                                marginRight: 0,
                            }}
                            onClick={() => setExpandedPopover({})}
                        >
                            <ListItemText primary={getMenuLabel(children)} />
                        </ListItemButton>
                    </Link>
                ))}
            </Popover>
        );
    };

    const list = () => {
        return (
            <>
                <List
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        padding: 0,
                        paddingTop: showDrawerFull ? "12px" : "8px",
                        overflowX: "hidden",
                        overflowY: "auto",
                        flexGrow: 1,
                    }}
                >
                    {getMenu().map((element, index) => {
                        if (element.children) {
                            const isExpandedParent =
                                element.id === expanded || element.children.some((e) => e.id === expanded);

                            return (
                                <Box key={`${element.id}-${index}`}>
                                    <Tooltip title={!drawerMainLayoutReducer.isOpen ? element.label : ""} placement="right">
                                        <ListItemButton
                                            selected={isExpandedParent}
                                            onClick={(event) => onClickTooltip(element.id, event)}
                                            sx={getMainItemSx(isExpandedParent)}
                                        >
                                            <ListItemIcon>{renderMenuIcon(element.id, element.icon)}</ListItemIcon>
                                            {drawerMainLayoutReducer.isOpen && (
                                                <>
                                                    <ListItemText primary={element.label} />
                                                    <ArrowForwardIos
                                                        sx={{
                                                            width: "14px",
                                                            height: "14px",
                                                            color: "#7a6a62",
                                                            transform: isExpandedParent
                                                                ? "rotate(90deg)"
                                                                : "rotate(0deg)",
                                                            transition: "transform 200ms ease-in-out",
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </ListItemButton>
                                    </Tooltip>
                                    {drawerMainLayoutReducer.isOpen ? getCollapse(element) : getPopover(element)}
                                </Box>
                            );
                        }

                        if (!canAccessRoute(element)) return null;

                        return (
                            <Box key={`${element.id}-${index}`}>
                                <Tooltip title={!drawerMainLayoutReducer.isOpen ? element.label : ""} placement="right">
                                    <Link to={element.path}>
                                        <ListItemButton
                                            selected={element.id === route.id}
                                            sx={getMainItemSx(element.id === route.id)}
                                        >
                                            <ListItemIcon>{element.icon}</ListItemIcon>
                                            {drawerMainLayoutReducer.isOpen && <ListItemText primary={element.label} />}
                                        </ListItemButton>
                                    </Link>
                                </Tooltip>
                            </Box>
                        );
                    })}
                </List>
                {showDrawerFull && (
                    <List sx={{ padding: 0, paddingTop: "8px" }}>
                        <ListItemButton onClick={logout} sx={getMainItemSx(false)}>
                            <ListItemIcon>
                                <Logout />
                            </ListItemIcon>
                            {drawerMainLayoutReducer.isOpen && <ListItemText primary="Déconnexion" />}
                        </ListItemButton>
                    </List>
                )}
            </>
        );
    };

    return (
        <DrawerMUI
            variant={showDrawerFull ? "permanent" : "temporary"}
            anchor={showDrawerFull ? "left" : "top"}
            open={drawerMainLayoutReducer.isOpen}
            onClose={() => dispatch(DrawerMainLayoutClose())}
            sx={{
                height: showDrawerFull ? "100%" : "max-content",
                flexShrink: 0,
                whiteSpace: "nowrap",
                boxSizing: "border-box",
                width: showDrawerFull
                    ? drawerMainLayoutReducer.isOpen
                        ? drawerOpenWidth
                        : drawerCloseWidth
                    : "100%",
                transition: "width 220ms ease-in-out",
                "& .MuiDrawer-paperAnchorDockedLeft": {
                    border: 0,
                },
                "& .MuiDrawer-paper": {
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    height: showDrawerFull ? "100%" : "auto",
                    width: "100%",
                    maxHeight: showDrawerFull ? "none" : "calc(100vh - 12px)",
                    padding: showDrawerFull ? "18px 12px 12px 12px" : "8px 12px 14px 12px",
                    boxSizing: "border-box",
                    overflowY: showDrawerFull ? "hidden" : "auto",
                    overflowX: "hidden",
                    borderRight: showDrawerFull ? "1px solid rgba(164, 139, 129, 0.22)" : "none",
                    borderBottom: !showDrawerFull ? "1px solid rgba(164, 139, 129, 0.22)" : "none",
                    borderRadius: showDrawerFull ? "0 22px 22px 0" : "0 0 18px 18px",
                    background:
                        "linear-gradient(180deg, #ffffff 0%, #fbf8f6 48%, rgba(236, 250, 236, 0.95) 100%)",
                    boxShadow: showDrawerFull
                        ? "8px 0 28px rgba(95, 74, 65, 0.12)"
                        : "0 10px 24px rgba(95, 74, 65, 0.18)",
                },
                "& .admin-sidebar-toggle": {
                    position: "absolute",
                    top: "50%",
                    left: "-7px",
                    transform: "translateY(-50%)",
                    width: "30px",
                    height: "64px",
                    borderRadius: "0 10px 10px 0",
                    border: "1px solid rgba(164, 139, 129, 0.35)",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 6px 12px rgba(86, 67, 58, 0.15)",
                    zIndex: 5,
                    padding: 0,
                    "&:hover": {
                        backgroundColor: "#ffffff",
                    },
                    "& svg": {
                        width: "16px",
                        height: "16px",
                        color: "#6d5951",
                    },
                },
            }}
        >
            {showDrawerFull ? (
                <IconButton
                    className={"admin-sidebar-toggle"}
                    onClick={() => changeDrawer(!drawerMainLayoutReducer.isOpen)}
                >
                    <ArrowBackIosNew
                        sx={{
                            transform: !drawerMainLayoutReducer.isOpen ? "rotate(0deg)" : "rotate(180deg)",
                            transition: "transform 220ms ease-in-out",
                        }}
                    />
                </IconButton>
            ) : (
                <Grid
                    container
                    spacing={0}
                    onClick={() => dispatch(DrawerMainLayoutClose())}
                    sx={{
                        width: "100%",
                        padding: "8px",
                        cursor: "pointer",
                        justifyContent: "flex-end",
                        color: "#7b6158",
                    }}
                >
                    <Close />
                </Grid>
            )}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: drawerMainLayoutReducer.isOpen ? "flex-start" : "center",
                    gap: "10px",
                    paddingX: drawerMainLayoutReducer.isOpen ? "8px" : "0px",
                    paddingTop: showDrawerFull ? "8px" : "0px",
                }}
            >
                <img
                    src={drawerMainLayoutReducer.isOpen ? "/asset/image/logo.webp" : "/asset/icon/logo192.png"}
                    alt="Logo"
                    style={{
                        height: drawerMainLayoutReducer.isOpen ? "40px" : "30px",
                        width: drawerMainLayoutReducer.isOpen ? "160px" : "52px",
                        objectFit: "contain",
                        display: "block",
                        transition: "all 180ms ease-in-out",
                    }}
                />
                {drawerMainLayoutReducer.isOpen && (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: "12px",
                                lineHeight: 1,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                color: "#8f6b5d",
                                fontWeight: 700,
                            }}
                        >
                            Back-Office
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 700,
                                color: "#4f3a32",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            Admin
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    marginTop: "14px",
                    marginX: drawerMainLayoutReducer.isOpen ? "4px" : "0px",
                    borderRadius: "14px",
                    border: "1px solid rgba(164, 139, 129, 0.30)",
                    backgroundColor: "rgba(255, 255, 255, 0.72)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: drawerMainLayoutReducer.isOpen ? "flex-start" : "center",
                    gap: "10px",
                    padding: drawerMainLayoutReducer.isOpen ? "10px" : "10px 0",
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        width: "34px",
                        height: "34px",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#5e4438",
                        backgroundColor: "rgba(228, 191, 179, 0.85)",
                    }}
                >
                    {userInitials}
                </Avatar>
                {drawerMainLayoutReducer.isOpen && (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 700,
                                color: "#4f3a32",
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {displayName || "Administrateur"}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#7a6860",
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {authReducer.email || "Utilisateur connecté"}
                        </Typography>
                    </Box>
                )}
            </Box>

            {list()}
        </DrawerMUI>
    );
}

export default Drawer;
